// import React from 'react';

// const Privacy: React.FC = () => (
//   <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-masa-blue/10 px-4">
//     <div className="bg-white/90 border border-masa-blue/20 rounded-2xl shadow-xl p-10 max-w-lg w-full text-center">
//       <div className="flex justify-center mb-6">
//         <span className="inline-block w-16 h-1 bg-gradient-to-r from-masa-coral via-masa-yellow via-masa-teal via-masa-blue to-masa-magenta rounded-full"></span>
//       </div>
//       <h1 className="text-3xl md:text-4xl font-bold mb-4 text-masa-dark">
//         Page Under Maintenance
//       </h1>
//       <p className="text-lg text-masa-blue font-semibold mb-2">Coming Soon!</p>
//       <p className="text-gray-600 mb-6">We're working hard to bring you this page. Please check back later.</p>
//       <div className="flex items-center justify-center space-x-2 mt-4">
//         <span className="w-3 h-3 bg-masa-coral rounded-full"></span>
//         <span className="w-3 h-3 bg-masa-yellow rounded-full"></span>
//         <span className="w-3 h-3 bg-masa-teal rounded-full"></span>
//         <span className="w-3 h-3 bg-masa-blue rounded-full"></span>
//         <span className="w-3 h-3 bg-masa-magenta rounded-full"></span>
//       </div>
//     </div>
//   </div>
// );

// export default Privacy;
import React from 'react';
import { Shield, FileText, Phone, Mail, MapPin } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
          <div className="flex items-center mb-6">
            <Shield className="h-8 w-8 text-blue-600 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy & Notice of Privacy Practices</h1>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-semibold">Masa Hills Health Services</p>
            <p className="text-blue-700">Effective Date: June 8, 2025</p>
          </div>
        </div>

        {/* Table of Contents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Table of Contents
          </h2>
          <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <a href="#introduction" className="text-blue-600 hover:text-blue-800 hover:underline">I. Introduction</a>
            <a href="#responsibilities" className="text-blue-600 hover:text-blue-800 hover:underline">II. Our Responsibilities Under HIPAA</a>
            <a href="#information" className="text-blue-600 hover:text-blue-800 hover:underline">III. Information We Collect</a>
            <a href="#usage" className="text-blue-600 hover:text-blue-800 hover:underline">IV. How We Use Health Information</a>
            <a href="#rights" className="text-blue-600 hover:text-blue-800 hover:underline">V. Your Rights</a>
            <a href="#security" className="text-blue-600 hover:text-blue-800 hover:underline">VI. Data Security</a>
            <a href="#texas-law" className="text-blue-600 hover:text-blue-800 hover:underline">VII. Texas Law Compliance</a>
            <a href="#children" className="text-blue-600 hover:text-blue-800 hover:underline">VIII. Children's Privacy</a>
            <a href="#changes" className="text-blue-600 hover:text-blue-800 hover:underline">IX. Changes to Policy</a>
            <a href="#contact" className="text-blue-600 hover:text-blue-800 hover:underline">X. Contact Information</a>
          </nav>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 space-y-8">
          
          {/* Section I */}
          <section id="introduction">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">I. Introduction</h2>
            <div className="prose prose-gray max-w-none space-y-4">
              <p>Welcome to Masa Hills Health Services ("we," "us," or "our"). This Privacy Policy applies to visitors of our website, www.masahillshealthservices.com (the "Site").</p>
              <p>Our commitment is to protect the privacy and security of your personal and health information. This document serves two purposes:</p>
              <ol className="list-decimal list-inside space-y-2 ml-4">
                <li>A Privacy Policy for our website, explaining how we handle information collected online.</li>
                <li>A Notice of Privacy Practices (NPP) as required by the Health Insurance Portability and Accountability Act of 1996 (HIPAA) and Texas law. This notice describes how your medical information may be used and disclosed and how you can get access to this information.</li>
              </ol>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                <p className="text-yellow-800 font-medium">By using our Site, you consent to the data practices described in this statement.</p>
              </div>
            </div>
          </section>

          {/* Section II */}
          <section id="responsibilities">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">II. Our Responsibilities Under HIPAA and Texas Law</h2>
            <div className="prose prose-gray max-w-none space-y-4">
              <p>As a healthcare provider, we are required by law to maintain the privacy of your Protected Health Information (PHI). PHI is information that can be used to identify you and that relates to your past, present, or future physical or mental health or condition, the provision of health care to you, or the payment for that care.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">We are required to:</h3>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>Ensure the confidentiality, integrity, and availability of all electronic PHI we receive, maintain, or transmit.</li>
                  <li>Provide you with this notice of our legal duties and privacy practices with respect to your PHI.</li>
                  <li>Follow the terms of the notice that is currently in effect.</li>
                  <li>Notify you in the event of a breach of your unsecured PHI.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section III */}
          <section id="information">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">III. Information We Collect</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">A. Information Collected Automatically by our Hosting Provider</h3>
                <p className="mb-3">Our website is hosted by HostGator (a Newfold brand). When you visit our Site, their servers may automatically collect certain information. This information does not identify you personally but is used for the operation of the service, to maintain quality of the service, and to provide general statistics regarding use of our Site.</p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Information collected automatically may include:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    <li>Your IP address</li>
                    <li>Browser type and operating system</li>
                    <li>Dates and times you access the site</li>
                    <li>The pages you visit, and the time spent on those pages</li>
                    <li>Referring website addresses</li>
                  </ul>
                </div>
                <p className="mt-3 text-sm text-gray-600">This data is collected and managed according to the Newfold Privacy Policy. We do not have direct control over this data collection.</p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">B. Information You Voluntarily Provide</h3>
                <p className="mb-3">We only collect personally identifiable information when you voluntarily provide it to us. For example, you may provide us with information through:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Contact Forms:</strong> If you use a contact form on our site, we may collect your name, email address, phone number, and any message you choose to send.</li>
                  <li><strong>Appointment Request Forms:</strong> We may collect your name, contact information, preferred appointment times, and the reason for your visit.</li>
                </ul>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                  <p className="text-green-800"><strong>Important Note:</strong> The Site itself does not store your personal information on its servers. When you submit information through a form, it is transmitted to us (for example, via secure email) so we can respond to your inquiry. We protect this information as PHI once we receive it.</p>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">C. Use of Cookies</h3>
                <p>Our Site may use "cookies" to help personalize your online experience. A cookie is a text file that is placed on your hard disk by a web page server. Cookies cannot be used to run programs or deliver viruses to your computer. You have the ability to accept or decline cookies. Most web browsers automatically accept cookies, but you can usually modify your browser setting to decline cookies if you prefer.</p>
              </div>
            </div>
          </section>

          {/* Section IV */}
          <section id="usage">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">IV. How We Use and Disclose Your Health Information</h2>
            <p className="mb-6">Under HIPAA and Texas law, we may use and disclose your PHI without your authorization for the following purposes:</p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">A. For Treatment, Payment, and Health Care Operations</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Treatment</h4>
                    <p className="text-blue-800 text-sm">We may use your PHI to provide, coordinate, and manage your health care. For example, we may share information with another physician who is treating you.</p>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Payment</h4>
                    <p className="text-green-800 text-sm">We may use and disclose your PHI to bill and collect payment from you, your insurance company, or another third party.</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-2">Health Care Operations</h4>
                    <p className="text-purple-800 text-sm">We may use and disclose your PHI for our business operations. This includes activities like quality assessment, employee review, and licensing.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">B. Other Permitted and Required Disclosures Without Your Authorization</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="mb-3">We may also use or disclose your PHI in the following situations without your authorization, as permitted or required by law:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 text-sm">
                    <li><strong>As Required by Law:</strong> We will disclose PHI when required to do so by federal, state, or local law.</li>
                    <li><strong>Public Health and Safety:</strong> To prevent or control disease, injury, or disability; to report births and deaths; to report child abuse or neglect.</li>
                    <li><strong>Health Oversight Activities:</strong> For audits, investigations, inspections, and licensure by government agencies.</li>
                    <li><strong>Lawsuits and Disputes:</strong> In response to a court order, subpoena, or discovery request.</li>
                    <li><strong>Law Enforcement:</strong> To identify or locate a suspect, fugitive, material witness, or missing person.</li>
                    <li><strong>Coroners, Medical Examiners, and Funeral Directors:</strong> To identify a deceased person or determine the cause of death.</li>
                    <li><strong>Workers' Compensation:</strong> To comply with workers' compensation laws.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">C. Disclosures Requiring Your Written Authorization</h3>
                <p>All other uses and disclosures of PHI not covered by this notice or the laws that apply to us will be made only with your written permission ("authorization"). You may revoke this authorization at any time, in writing, except to the extent that we have already acted in reliance on your permission.</p>
              </div>
            </div>
          </section>

          {/* Section V */}
          <section id="rights">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">V. Your Rights Regarding Your Protected Health Information</h2>
            <p className="mb-4">You have the following rights regarding the PHI we maintain about you:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2">Right to Inspect and Copy</h4>
                <p className="text-blue-800 text-sm">You have the right to inspect and obtain a copy of your health and billing records. You must submit your request in writing. We may charge a reasonable, cost-based fee.</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h4 className="font-semibold text-green-900 mb-2">Right to Amend</h4>
                <p className="text-green-800 text-sm">If you believe that PHI we have about you is incorrect or incomplete, you may ask us to amend it. You must make this request in writing and provide a reason.</p>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-purple-900 mb-2">Right to an Accounting of Disclosures</h4>
                <p className="text-purple-800 text-sm">You have the right to request a list of the disclosures we have made of your PHI for purposes other than treatment, payment, and health care operations.</p>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-yellow-900 mb-2">Right to Request Restrictions</h4>
                <p className="text-yellow-800 text-sm">You have the right to request a restriction on the PHI we use or disclose for treatment, payment, or health care operations.</p>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-red-900 mb-2">Right to Request Confidential Communications</h4>
                <p className="text-red-800 text-sm">You have the right to request that we communicate with you about medical matters in a certain way or at a certain location.</p>
              </div>
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h4 className="font-semibold text-indigo-900 mb-2">Right to a Paper Copy of This Notice</h4>
                <p className="text-indigo-800 text-sm">You have the right to a paper copy of this notice at any time, even if you have agreed to receive this notice electronically.</p>
              </div>
            </div>
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4 mt-4">
              <p className="text-gray-800 font-medium">To exercise any of these rights, please submit a written request to our Privacy Officer at the contact information listed below.</p>
            </div>
          </section>

          {/* Section VI */}
          <section id="security">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">VI. Data Security</h2>
            <div className="space-y-4">
              <p>We take our responsibility to protect your information seriously. We have implemented administrative, physical, and technical safeguards to protect your PHI. When you submit information via our website forms, that information is protected using encryption, such as the Secure Sockets Layer (SSL) protocol, during transmission.</p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800">Our web hosting provider, HostGator, is responsible for the security of the servers where the Site is hosted. As required by HIPAA, we will have a Business Associate Agreement (BAA) in place with any third-party service provider, including our web host, where they may have potential access to PHI.</p>
              </div>
            </div>
          </section>

          {/* Section VII */}
          <section id="texas-law">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">VII. Compliance with Texas Law</h2>
            <p className="mb-4">We are committed to complying with all applicable Texas laws, including:</p>
            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-semibold text-gray-900">Texas Health and Safety Code, Chapter 181 (Texas Medical Records Privacy Act)</h4>
                <p className="text-gray-700 text-sm">This law provides protections that are similar to, and in some cases stricter than, HIPAA. We will follow the more protective standard where the laws differ.</p>
              </div>
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-semibold text-gray-900">Texas Deceptive Trade Practices Act (DTPA)</h4>
                <p className="text-gray-700 text-sm">This Privacy Policy is our commitment to you. We strive to be transparent and accurate in how we describe our data handling practices. We will not engage in any false, misleading, or deceptive acts in our statements to you.</p>
              </div>
            </div>
          </section>

          {/* Section VIII */}
          <section id="children">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">VIII. Children's Privacy</h2>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-orange-800">Our Site is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it.</p>
            </div>
          </section>

          {/* Section IX */}
          <section id="changes">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">IX. Changes to This Privacy Policy</h2>
            <p>We reserve the right to change our privacy practices and the terms of this notice at any time. Any revised notice will be effective for all PHI we already have about you as well as any information we receive in the future. We will post a copy of the current notice on our website and a copy will be available at our office.</p>
          </section>

          {/* Section X */}
          <section id="contact">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">X. Contact Information and Complaints</h2>
            <p className="mb-4">If you have any questions about this Privacy Policy or wish to exercise your rights, please contact us at:</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-bold text-blue-900 mb-4">Masa Hills Health Services LLC</h3>
              <div className="space-y-3">
                <div className="flex items-center text-blue-800">
                  <MapPin className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span>3410 Texas Dr, Sachse, Texas 75048</span>
                </div>
                <div className="flex items-center text-blue-800">
                  <Phone className="h-5 w-5 mr-3 flex-shrink-0" />
                  <span>(469) 573-0176</span>
                </div>
                <div className="flex items-center text-blue-800">
                  <Mail className="h-5 w-5 mr-3 flex-shrink-0" />
                  <a href="mailto:Contact@masahillshealthservices.com" className="hover:underline">
                    Contact@masahillshealthservices.com
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          <p>© 2025 Masa Hills Health Services LLC. All rights reserved.</p>
          <p className="mt-1">This privacy policy is effective as of June 8, 2025.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;